import React, { useState, useEffect } from 'react';
import { CalendarBlank, FileText, MagnifyingGlassPlus } from 'phosphor-react';
import { SkinPhoto } from '../../types/entities';
import { formatDate } from '../../utils/date';
import usePresence, { useUltimoValor } from '../../use/usePresence';
import LazyImage from '../ui/LazyImage';
import Lightbox from '../ui/Lightbox';

interface SkinPhotoDetailModalProps {
  /** null fecha o modal. O conteúdo sobrevive à animação de saída. */
  photo: SkinPhoto | null;
  onClose: () => void;
}

export default function SkinPhotoDetailModal({ photo: fotoProp, onClose }: SkinPhotoDetailModalProps) {
  const [lightbox, setLightbox] = useState(false);
  const photo = useUltimoValor(fotoProp);
  const { montado, estado } = usePresence(Boolean(fotoProp), 200);

  // Agora o componente fica montado o tempo todo: o zoom precisa ser zerado à mão.
  useEffect(() => {
    if (!fotoProp) setLightbox(false);
  }, [fotoProp]);

  if (!montado || !photo) return null;

  return (
    <div
      data-state={estado}
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end justify-center p-4 data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out data-[state=closed]:pointer-events-none"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        data-state={estado}
        className="bg-white dark:bg-gray-800 w-full max-w-lg rounded-3xl overflow-hidden shadow-2xl max-h-[92vh] flex flex-col data-[state=open]:animate-sheet-in data-[state=closed]:animate-sheet-out"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="relative group cursor-zoom-in"
          onClick={() => setLightbox(true)}
        >
          <LazyImage
            src={photo.photoUrl}
            alt={`Pele em ${formatDate(photo.photoDate)}`}
            eager
            className="w-full h-80 bg-nude-50 dark:bg-gray-700"
            imgClassName="w-full h-full object-contain"
          />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
            <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 text-white px-3 py-1.5 rounded-xl flex items-center gap-1.5 text-xs font-semibold">
              <MagnifyingGlassPlus className="w-3.5 h-3.5" />
              Toque para ampliar
            </div>
          </div>
        </div>
        <div className="px-5 py-4 flex flex-col gap-3 overflow-y-auto">
          <div className="flex items-center gap-2">
            <CalendarBlank className="w-4 h-4 text-blush-400" />
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">
              {formatDate(photo.photoDate)}
            </p>
          </div>

          {photo.notes ? (
            <div className="flex items-start gap-2">
              <FileText className="w-4 h-4 text-nude-400 dark:text-gray-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{photo.notes}</p>
            </div>
          ) : (
            <p className="text-xs text-nude-300 dark:text-gray-500 italic">
              Sem anotações neste registro.
            </p>
          )}

          <button
            onClick={onClose}
            className="mt-1 w-full py-3 bg-nude-100 dark:bg-gray-700 hover:bg-nude-200 dark:hover:bg-gray-600 text-nude-600 dark:text-gray-300 font-semibold text-sm rounded-xl transition-colors"
          >
            Fechar
          </button>
        </div>
      </div>

      <Lightbox aberto={lightbox} onClose={() => setLightbox(false)}>
        <LazyImage
          src={photo.photoUrl}
          alt={`Pele em ${formatDate(photo.photoDate)}`}
          eager
          className="flex items-center justify-center"
          imgClassName="max-w-full max-h-[90vh] rounded-2xl object-contain shadow-2xl"
        />
      </Lightbox>
    </div>
  );
}
